import styled, { css } from 'styled-components';

import { size } from '../../layout/helpers';
import { Icon } from '../Icon';

export interface ButtonProps {
  $tone?:
    | 'primary'
    | 'primaryClear'
    | 'primaryDashed'
    | 'secondary'
    | 'accent'
    | 'base'
    | 'baseClear'
    | 'outline'
    | 'info'
    | 'success'
    | 'warning'
    | 'error'
    | 'errorClear';
  $size?: 'tiny' | 'small'; // TODO: Remove if not used
  $variant?: 'square';
  $fullWidth?: boolean;
  $inputHeight?: boolean;
}

export const Button = styled.button<ButtonProps>`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${size(0.5)};
  min-height: ${size(5.5)};
  min-width: fit-content;
  padding: ${size(1.5)} ${size(3)};
  margin: 0;
  border: 0;
  border-radius: 100vmax;
  background-color: var(--button-bg, transparent);
  color: var(--button-fg, var(--c-text-main));
  -webkit-tap-highlight-color: transparent;
  font-size: ${(props) => props.theme.font.size.small};
  line-height: ${(props) => props.theme.font.lineHeight.main};
  font-weight: var(--font-weight-bold);
  text-decoration: none;
  transform-origin: 50% 50%;
  transition:
    transform 200ms ease,
    background-color 150ms ease;
  user-select: none;
  cursor: pointer;

  &::before {
    content: '';
    position: absolute;
    inset: 1px;
    background-color: rgba(0, 0, 0, 0.4);
    border-radius: inherit;
    mix-blend-mode: soft-light;
    pointer-events: none;
    opacity: 0;
    transition: opacity 200ms ease;
  }

  &:enabled {
    &:active {
      transform: scale(0.97);
    }

    &:hover {
      &::before {
        opacity: 1;
      }
    }
  }

  &[disabled] {
    opacity: 0.37;
    cursor: not-allowed;
  }

  ${({ $tone }) =>
    $tone === 'primary'
      ? css`
          --button-bg: var(--c-primary-bg);
          --button-fg: var(--c-primary-fg);
        `
      : $tone === 'primaryClear'
        ? css`
            --button-bg: transparent;
            --button-fg: var(--c-primary-bg);

            // Disable hover overlay due to bug with mix-blend-mode and transparent background
            &::before {
              content: none;
            }
          `
        : $tone === 'primaryDashed'
          ? css`
              --button-bg: transparent;
              --button-fg: var(--c-secondary-fg);

              border: 1px dashed var(--c-border-alt);

              // Disable hover overlay due to bug with mix-blend-mode and transparent background
              &::before {
                content: none;
              }
            `
          : $tone === 'secondary'
            ? css`
                --button-bg: var(--c-secondary-bg);
                --button-fg: var(--c-secondary-fg);
              `
            : $tone === 'accent'
              ? css`
                  --button-bg: var(--c-accent-bg);
                  --button-fg: var(--c-accent-fg);
                `
              : $tone === 'base'
                ? css`
                    --button-bg: var(--c-base-bg);
                    --button-fg: var(--c-base-fg);
                  `
                : $tone === 'outline'
                  ? css`
                    --button-bg: transparent;
                    --button-fg: var(--c-text-main);

                    border: 1px solid var(--c-border-main);

                    // Disable hover overlay due to bug with mix-blend-mode and transparent background
                    &::before {
                      content: none;
                    }
                  `
                  : $tone === 'info'
                    ? css`
                      --button-bg: var(--c-semantic-info-100);
                      --button-fg: var(--c-semantic-info-500);
                    `
                    : $tone === 'success'
                      ? css`
                      --button-bg: var(--c-semantic-success-300);
                      --button-fg: var(--c-semantic-success-700);
                    `
                      : $tone === 'warning'
                        ? css`
                        --button-bg: var(--c-semantic-warning-300);
                        --button-fg: var(--c-semantic-warning-700);
                      `
                        : $tone === 'error'
                          ? css`
                          --button-bg: var(--c-semantic-error-300);
                          --button-fg: var(--c-semantic-error-700);
                        `
                          : $tone === 'errorClear'
                            ? css`
                            --button-bg: transparent;
                            --button-fg: var(--c-semantic-error-500);

                            // Disable hover overlay due to bug with mix-blend-mode and transparent background
                            &::before {
                              content: none;
                            }
                          `
                            : css`
                            // Disable hover overlay due to bug with mix-blend-mode and transparent background
                            &::before {
                              content: none;
                            }
                          `}

  ${({ $variant }) =>
    $variant === 'square'
      ? css`
          padding-inline: ${size(2)};
          border-radius: ${size(0.5)};
        `
      : undefined}

  // TODO: Remove if not used
  ${({ $size }) =>
    $size === 'tiny'
      ? css`
          min-height: 2rem;
          padding: 0.25rem 0.75rem;
          font-size: ${(props) => props.theme.font.size.xsmall};
        `
      : $size === 'small'
        ? css`
            min-height: 2.25rem;
            padding: 0.5rem 1rem;
            font-size: ${(props) => props.theme.font.size.xsmall};
          `
        : undefined}

  ${({ $fullWidth }) =>
    $fullWidth &&
    css`
      width: 100%;
    `}

  ${({ $inputHeight }) =>
    $inputHeight &&
    css`
      height: ${(props) => props.theme.distances.inputHeight};
    `}

  ${Icon},
  svg {
    font-size: ${(props) => props.theme.font.size.xlarge};
  }

  svg {
    display: block;
  }
`;

// TODO: Remove
export const ButtonContent = styled.span`
  display: block;

  & + & {
    margin-left: ${size(1)};
  }
`;
